import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ChevronRight, ChevronLeft, Check } from "lucide-react";
import api from "@/lib/api";
import { BrandMark } from "@/components/BrandMark.jsx";
import { fmtIST } from "@/lib/time";

/** Public candidate form with two-column desktop layout + mobile-first fallback. */
function Field({ block, value, onChange }) {
  const base = "tt-input !py-3 text-base";
  const k = block.kind;
  const tid = `public-form-input-${block.block_id}`;
  if (k === "short_text") return <input className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={block.placeholder || ""} data-testid={tid} />;
  if (k === "long_text") return <textarea rows={5} className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  if (k === "email") return <input type="email" className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="you@domain.com" data-testid={tid} />;
  if (k === "phone") return <input type="tel" className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="+91…" data-testid={tid} />;
  if (k === "url") return <input type="url" className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" data-testid={tid} />;
  if (k === "number" || k === "currency") return <input type="number" className={base} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={tid} />;
  if (k === "city") return <input className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Delhi…" data-testid={tid} />;
  if (k === "date") return <input type="date" className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  if (k === "yes_no") return (
    <div className="flex gap-2" data-testid={tid}>
      {["Yes","No"].map((o) => <button key={o} type="button" onClick={() => onChange(o)} className={`flex-1 min-h-[52px] px-4 rounded-md border transition-colors ${value === o ? "border-[#5B9BFF] bg-[rgba(91,155,255,0.1)]" : "border-[#262626] bg-[#121212] hover:border-[#3F3F46] text-[#A1A1AA]"}`}>{o}</button>)}
    </div>
  );
  if (k === "consent") return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={tid}>
      <input type="checkbox" className="mt-1 accent-[#5B9BFF] h-5 w-5" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-[#A1A1AA]">{block.label}</span>
    </label>
  );
  if (k === "rating") {
    const v = typeof value === "number" ? value : 0;
    return <div className="flex gap-1" data-testid={tid}>{[1,2,3,4,5].map((n) => <button key={n} type="button" onClick={() => onChange(n)} className={`h-12 w-12 rounded-md border font-mono ${v >= n ? "border-[#5B9BFF] bg-[rgba(91,155,255,0.12)] text-[#5B9BFF]" : "border-[#262626] bg-[#121212] text-[#A1A1AA]"}`}>{n}</button>)}</div>;
  }
  if (k === "single_choice") return (
    <div className="space-y-2" data-testid={tid}>
      {(block.options || []).map((o) => <button key={o} type="button" onClick={() => onChange(o)} className={`w-full text-left min-h-[52px] px-4 rounded-md border transition-colors ${value === o ? "border-[#5B9BFF] bg-[rgba(91,155,255,0.1)]" : "border-[#262626] bg-[#121212] hover:border-[#3F3F46]"}`}>{o}</button>)}
    </div>
  );
  if (k === "multi_choice") {
    const v = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(v.includes(o) ? v.filter((x) => x !== o) : [...v, o]);
    return <div className="space-y-2" data-testid={tid}>{(block.options || []).map((o) => <button key={o} type="button" onClick={() => toggle(o)} className={`w-full text-left min-h-[52px] px-4 rounded-md border ${v.includes(o) ? "border-[#5B9BFF] bg-[rgba(91,155,255,0.1)]" : "border-[#262626] bg-[#121212] hover:border-[#3F3F46]"}`}>{o}</button>)}</div>;
  }
  if (k === "dropdown") return (
    <select className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">— choose —</option>
      {(block.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (k === "file_upload") return <input type="file" className="text-sm text-[#A1A1AA] file:bg-[#1A1A1A] file:border file:border-[#262626] file:text-[#F4F4F5] file:px-3 file:py-2 file:rounded-md" onChange={async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/upload", fd); onChange({ file_id: r.data.file_id, name: r.data.original_name }); } catch { onChange(file.name); }
  }} data-testid={tid} />;
  return <div className="text-xs text-[#71717A]">[{k}]</div>;
}

function BookingStep({ formId, submissionId, booking, onBook }) {
  const [slots, setSlots] = useState([]);
  const [chosenDay, setChosenDay] = useState(null);
  const [chosen, setChosen] = useState(booking?.slot_iso || null);
  useEffect(() => { (async () => { const r = await api.get(`/public/booking/${formId}/slots`); setSlots(r.data.slots); if (r.data.slots[0]) setChosenDay(r.data.slots[0].date); })(); }, [formId]);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!chosen) return;
    setBusy(true);
    try { const r = await api.post("/public/booking/book", { submission_id: submissionId, slot_iso: chosen }); onBook(r.data); }
    catch (e) { window.alert(e?.response?.data?.detail || "Booking failed. Please try again."); }
    finally { setBusy(false); }
  };
  const day = slots.find((d) => d.date === chosenDay);
  return (
    <div className="space-y-4">
      {!booking && <><div className="flex gap-2 overflow-x-auto pb-1">
        {slots.map((d) => <button key={d.date} onClick={() => setChosenDay(d.date)} className={`min-w-[64px] px-3 py-2 rounded-md border ${chosenDay === d.date ? "border-[#5B9BFF] bg-[rgba(91,155,255,0.1)]" : "border-[#262626] bg-[#121212] text-[#A1A1AA]"}`}><div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{d.weekday}</div><div className="text-sm font-mono">{d.day_label}</div></button>)}
      </div>
      {day && <div className="grid grid-cols-3 lg:grid-cols-4 gap-2">{day.slots.map((s) => <button key={s.iso} disabled={!s.available} onClick={() => setChosen(s.iso)} className={`min-h-[52px] rounded-md border font-mono text-sm ${chosen === s.iso ? "border-[#5B9BFF] bg-[rgba(91,155,255,0.12)] text-[#5B9BFF]" : s.available ? "border-[#262626] bg-[#121212] text-[#F4F4F5] hover:border-[#3F3F46]" : "border-[#1E1E1E] bg-[#0F0F0F] text-[#52525B] cursor-not-allowed"}`}>{s.label}</button>)}</div>}
      {slots.length === 0 && <p className="text-sm text-[#71717A]">No open slots right now. We'll email you to schedule.</p>}
      <button onClick={confirm} disabled={!chosen || busy} className="tt-btn-primary w-full !py-3 disabled:opacity-50" data-testid="booking-confirm">{busy ? "Booking…" : "Confirm interview slot"}</button></>}
      {booking && <div className="tt-card p-4 border-[#22C55E]/40" data-testid="booking-confirmed"><div className="flex items-center gap-2 text-[#22C55E]"><Check size={16} /> Interview confirmed</div><div className="text-sm mt-1">{fmtIST(booking.slot_iso)}</div>{booking.meet_link ? <a href={booking.meet_link} target="_blank" rel="noreferrer" className="text-[#5B9BFF] text-sm underline">{booking.meet_link}</a> : <div className="text-sm text-[#A1A1AA] mt-1">We'll email you a calendar invite with the Google Meet link.</div>}<p className="text-sm text-[#A1A1AA] mt-3">Thanks for applying. You can close this page now.</p></div>}
    </div>
  );
}

export default function PublicForm() {
  const { slug } = useParams();
  const location = useLocation();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [resumeToken, setResumeToken] = useState(() => localStorage.getItem(`tt_resume_${slug}`) || null);
  const [submissionId, setSubmissionId] = useState(null);
  const [done, setDone] = useState(null);
  const [booking, setBooking] = useState(null);
  const startedAt = useRef(Date.now());

  const utm = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return { source: q.get("utm_source"), medium: q.get("utm_medium"), campaign: q.get("utm_campaign"), ref: q.get("ref") };
  }, [location.search]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/public/forms/${slug}`, { params: utm }); setForm(r.data); } catch { setError("Form not found"); return; }
      const q = new URLSearchParams(location.search);
      const rtoken = q.get("r") || localStorage.getItem(`tt_resume_${slug}`);
      if (rtoken) {
        try { const rs = await api.get(`/public/submissions/${rtoken}`); setAnswers(rs.data.answers || {}); setStep(rs.data.last_step_reached || 1); setName(rs.data.name || ""); setEmail(rs.data.email || ""); setSubmissionId(rs.data.submission_id); setResumeToken(rtoken); } catch { localStorage.removeItem(`tt_resume_${slug}`); }
      }
    })();
    // eslint-disable-next-line
  }, [slug]);

  const steps = useMemo(() => { if (!form) return [1]; const s = new Set(); (form.blocks || []).forEach((b) => s.add(b.step || 1)); return Array.from(s).sort((a, b) => a - b); }, [form]);
  const totalSteps = steps.length || 1;
  const stepIdx = steps.indexOf(step);
  const needsBooking = useMemo(() => (form?.blocks || []).some((b) => b.kind === "calendar_booking" && (b.step || 1) === steps[steps.length - 1]), [form, steps]);
  const currentBlocks = useMemo(() => (form?.blocks || []).filter((b) => (b.step || 1) === step), [form, step]);
  const stepTitles = useMemo(() => steps.map((n) => { const head = (form?.blocks || []).find((b) => (b.step||1) === n && b.kind === "heading"); return head?.label || `Step ${n}`; }), [steps, form]);

  const setAns = (bid, val) => setAnswers((a) => ({ ...a, [bid]: val }));

  const persist = async (partial) => {
    const payload = { form_id: form.form_id, email: email || null, name: name || null, phone: phone || null, city: city || null, answers, total_time_ms: Date.now() - startedAt.current, last_step_reached: step, partial, utm, resume_token: resumeToken || undefined };
    const r = await api.post("/public/submissions", payload);
    setSubmissionId(r.data.submission_id); setResumeToken(r.data.resume_token);
    if (partial && !r.data.already_submitted) localStorage.setItem(`tt_resume_${slug}`, r.data.resume_token);
    else localStorage.removeItem(`tt_resume_${slug}`);
    return r.data;
  };
  const validateStep = () => currentBlocks.find((b) => b.required && (answers[b.block_id] === undefined || answers[b.block_id] === "" || (Array.isArray(answers[b.block_id]) && !answers[b.block_id].length) || answers[b.block_id] === false));

  useEffect(() => {
    if (!form) return;
    const nameBlock = (form.blocks || []).find((b) => b.kind === "short_text" && /name/i.test(b.label || ""));
    const emailBlock = (form.blocks || []).find((b) => b.kind === "email");
    const phoneBlock = (form.blocks || []).find((b) => b.kind === "phone");
    const cityBlock = (form.blocks || []).find((b) => b.kind === "city");
    if (nameBlock && answers[nameBlock.block_id]) setName(answers[nameBlock.block_id]);
    if (emailBlock && answers[emailBlock.block_id]) setEmail(answers[emailBlock.block_id]);
    if (phoneBlock && answers[phoneBlock.block_id]) setPhone(answers[phoneBlock.block_id]);
    if (cityBlock && answers[cityBlock.block_id]) setCity(answers[cityBlock.block_id]);
    // eslint-disable-next-line
  }, [answers, form]);

  const next = async () => {
    const miss = validateStep();
    if (miss) { window.alert(`Please answer: ${miss.label}`); return; }
    const isLast = stepIdx === steps.length - 1;
    try { if (isLast) { const r = await persist(false); setDone(r); } else { await persist(true); setStep(steps[stepIdx + 1]); } }
    catch { window.alert("Save failed. Please try again."); }
  };
  const prev = () => { if (stepIdx > 0) setStep(steps[stepIdx - 1]); };

  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey && !["TEXTAREA"].includes(e.target.tagName)) { e.preventDefault(); next(); } };

  if (error) return <Layout><div className="text-center text-sm text-[#71717A] py-20">Form not found.</div></Layout>;
  if (!form) return <Layout><div className="text-center text-[#71717A] py-20 font-mono text-xs">LOADING…</div></Layout>;

  // Terminal states
  if (done?.knockout) return <Layout form={form}><div data-testid="public-form-knockout" className="py-10"><div className="tt-label mb-2">Thanks for applying</div><h2 className="text-xl font-semibold mb-3">{form.settings.knockout_message}</h2></div></Layout>;
  const gate = form.settings?.score_gate_for_booking;
  const gated = done && gate && typeof done.ai_score === "number" && done.ai_score < gate && !done.booking;
  if (done && (!needsBooking || gated)) return <Layout form={form}><div data-testid="public-form-thank-you" className="py-10"><div className="h-12 w-12 rounded-full bg-[rgba(34,197,94,0.12)] text-[#22C55E] flex items-center justify-center mb-4"><Check size={22} /></div><h2 className="text-xl font-semibold mb-3">{form.settings.thank_you_message}</h2><p className="text-sm text-[#A1A1AA]">We'll be in touch at <span className="font-mono text-[#F4F4F5]">{email}</span>.</p></div></Layout>;
  if (done && needsBooking) return <Layout form={form} stepTitles={stepTitles} activeStep={stepIdx}><div className="mb-4"><div className="tt-label mb-1">Final step</div><h2 className="text-xl font-semibold tracking-tight">Pick an interview slot</h2><p className="text-sm text-[#A1A1AA] mt-1">30-minute intro call. IST times.</p></div><BookingStep formId={form.form_id} submissionId={done.submission_id || submissionId} booking={booking || done.booking} onBook={setBooking} /></Layout>;

  return (
    <Layout form={form} stepTitles={stepTitles} activeStep={stepIdx} progressPct={(stepIdx + 1) / totalSteps * 100}>
      <div data-testid={`public-form-step-${step}`} onKeyDown={onKeyDown}>
        <div className="mb-6">
          <div className="tt-label">Step {stepIdx + 1} of {totalSteps}</div>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight mt-1">{form.title}</h1>
          {form.role && <div className="text-sm text-[#71717A] mt-0.5">{form.role}</div>}
        </div>
        <div className="space-y-6">
          {currentBlocks.map((b) => {
            if (b.kind === "heading") return <h2 key={b.block_id} className="text-lg font-semibold tracking-tight">{b.label}</h2>;
            if (b.kind === "rich_text") return <p key={b.block_id} className="text-sm text-[#A1A1AA] leading-relaxed">{b.label}</p>;
            if (b.kind === "page_break") return null;
            if (b.kind === "calendar_booking") return <p key={b.block_id} className="text-sm text-[#71717A]">Interview booking is the final step.</p>;
            return (
              <div key={b.block_id}>
                <label className="block text-sm font-medium mb-2 text-[#F4F4F5]">{b.label} {b.required && <span className="text-[#F5A524]">*</span>}</label>
                {b.help_text && <div className="text-xs text-[#71717A] mb-2">{b.help_text}</div>}
                <Field block={b} value={answers[b.block_id]} onChange={(v) => setAns(b.block_id, v)} />
              </div>
            );
          })}
        </div>
        <div className="mt-8 flex gap-2">
          {stepIdx > 0 && <button data-testid="public-form-prev" onClick={prev} className="tt-btn-secondary flex items-center gap-1 !py-3"><ChevronLeft size={14} /> Back</button>}
          <button data-testid={stepIdx === steps.length - 1 ? "public-form-submit" : "public-form-next"} onClick={next} className="tt-btn-primary flex-1 flex items-center justify-center gap-1 !py-3">{stepIdx === steps.length - 1 ? "Submit application" : "Continue"} <ChevronRight size={14} /></button>
        </div>
        <div className="mt-6 text-xs text-[#71717A]">Autosaving as you go. Press Enter to continue.</div>
      </div>
    </Layout>
  );
}

function Layout({ children, form, stepTitles = [], activeStep = 0, progressPct = 0 }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F4F4F5]">
      {/* Mobile top progress */}
      <div className="lg:hidden sticky top-0 z-10 bg-[#0A0A0A] border-b border-[#1E1E1E]">
        <div className="h-0.5 bg-[#121212]"><div className="h-full bg-[#5B9BFF] transition-all duration-300" style={{ width: `${progressPct || ((activeStep + 1) / Math.max(1, stepTitles.length) * 100)}%` }} /></div>
        <div className="px-5 py-3 flex items-center justify-between">
          <BrandMark size="md" />
          {stepTitles.length > 0 && <span className="font-mono text-[10px] text-[#71717A]">{activeStep + 1} / {stepTitles.length}</span>}
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[380px_minmax(0,1fr)] lg:min-h-screen">
        {/* Desktop sticky left panel */}
        <aside className="hidden lg:flex lg:flex-col lg:sticky lg:top-0 lg:h-screen bg-[#0F0F0F] border-r border-[#1E1E1E] p-10">
          <BrandMark size="xl" className="self-start" />
          <div className="mt-10">
            <div className="font-mono text-[10px] tracking-[0.2em] text-[#71717A] uppercase mb-2">Application</div>
            <h1 className="text-2xl font-semibold tracking-tight mb-1">{form?.title || "Form"}</h1>
            {form?.role && <div className="text-sm text-[#A1A1AA]">{form.role}</div>}
          </div>
          <p className="text-sm text-[#71717A] mt-6 leading-relaxed">Takes about 5-10 minutes. You can save and resume via a link we email you.</p>
          {stepTitles.length > 0 && (
            <div className="mt-10">
              <div className="font-mono text-[10px] tracking-[0.2em] text-[#71717A] uppercase mb-3">Steps</div>
              <ol className="space-y-2">
                {stepTitles.map((t, i) => (
                  <li key={i} className={`flex items-start gap-3 text-sm ${i === activeStep ? "text-[#F4F4F5]" : i < activeStep ? "text-[#71717A]" : "text-[#52525B]"}`}>
                    <div className={`mt-0.5 h-5 w-5 rounded-full border grid place-items-center text-[10px] font-mono ${i === activeStep ? "border-[#5B9BFF] text-[#5B9BFF]" : i < activeStep ? "border-[#22C55E] text-[#22C55E]" : "border-[#262626]"}`}>{i < activeStep ? "✓" : i + 1}</div>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="flex-1" />
          <div className="font-mono text-[10px] tracking-[0.22em] text-[#71717A] uppercase">team.thickandthin.media</div>
        </aside>

        <main className="px-5 py-8 lg:px-16 lg:py-16">
          <div className="max-w-[760px] mx-auto lg:mx-0">{children}</div>
        </main>
      </div>
    </div>
  );
}
